import React, { useState, useEffect } from 'react';
import { Range } from 'react-range';
import { FaChevronDown, FaFilter, FaTimes } from 'react-icons/fa';
import formatCurrency from '../../../utils/formatCurrency';

export const DEFAULT_PRECIO_MIN = 10000;
export const DEFAULT_PRECIO_MAX = 1500000;

export const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevancia (Más vendidos)' },
  { value: 'price_asc', label: 'Menor precio' },
  { value: 'price_desc', label: 'Mayor precio' },
  { value: 'alpha_asc', label: 'Nombre (A-Z)' },
  { value: 'alpha_desc', label: 'Nombre (Z-A)' },
];

export const DEFAULT_RESULT_FILTERS = {
  sort: 'relevance',
  precioMin: DEFAULT_PRECIO_MIN,
  precioMax: DEFAULT_PRECIO_MAX,
  /** null = sin filtro; true = stock 0; false = stock > 0 */
  agotado: null,
};

export const normalizePriceRange = (precioMin, precioMax) => {
  let min = Number(precioMin);
  let max = Number(precioMax);
  if (!Number.isFinite(min)) min = DEFAULT_PRECIO_MIN;
  if (!Number.isFinite(max)) max = DEFAULT_PRECIO_MAX;
  min = Math.max(DEFAULT_PRECIO_MIN, Math.min(min, DEFAULT_PRECIO_MAX));
  max = Math.max(DEFAULT_PRECIO_MIN, Math.min(max, DEFAULT_PRECIO_MAX));
  if (min > max) return [DEFAULT_PRECIO_MIN, DEFAULT_PRECIO_MAX];
  return [min, max];
};

export const countActiveResultFilters = (filters) => {
  const normalized = normalizePriceRange(
    filters?.precioMin ?? DEFAULT_PRECIO_MIN,
    filters?.precioMax ?? DEFAULT_PRECIO_MAX,
  );
  let count = 0;
  if (filters.sort && filters.sort !== 'relevance') count++;
  if (normalized[0] > DEFAULT_PRECIO_MIN) count++;
  if (normalized[1] < DEFAULT_PRECIO_MAX) count++;
  if (filters?.agotado === true || filters?.agotado === false) count++;
  return count;
};

const BuscadorResultFilters = ({
  resultFilters,
  onSortChange,
  onApplyPrice,
  onClearPriceFilter,
  isRefetching = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [priceRange, setPriceRange] = useState(() =>
    normalizePriceRange(resultFilters.precioMin, resultFilters.precioMax)
  );

  useEffect(() => {
    setPriceRange(normalizePriceRange(resultFilters.precioMin, resultFilters.precioMax));
  }, [resultFilters.precioMin, resultFilters.precioMax]);

  const activeCount = countActiveResultFilters(resultFilters);
  const [normalizedMin, normalizedMax] = normalizePriceRange(resultFilters.precioMin, resultFilters.precioMax);
  const priceMinActive = normalizedMin > DEFAULT_PRECIO_MIN;
  const priceMaxActive = normalizedMax < DEFAULT_PRECIO_MAX;

  const handlePriceChange = (values) => {
    setPriceRange(values);
  };

  const handleApplyPrice = () => {
    onApplyPrice({ precioMin: priceRange[0], precioMax: priceRange[1] });
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 shrink-0">
          Ordenar:
          <select
            value={resultFilters.sort}
            onChange={(e) => onSortChange(e.target.value)}
            disabled={isRefetching}
            className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-yellow-400 disabled:opacity-60"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold transition-colors ${
            isExpanded || activeCount > 0
              ? 'border-yellow-400 bg-yellow-50 text-yellow-800'
              : 'border-gray-300 bg-white text-gray-700 hover:border-yellow-400'
          }`}
        >
          <FaFilter className="text-xs" />
          Rango de precio
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-yellow-500 text-black text-xs font-bold">
              {activeCount}
            </span>
          )}
          <FaChevronDown className={`text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {(priceMinActive || priceMaxActive) && (
          <div className="flex flex-wrap items-center gap-2">
            {priceMinActive && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200">
                Min: {formatCurrency(normalizedMin)}
                <button
                  type="button"
                  onClick={() => onClearPriceFilter('min')}
                  className="hover:text-indigo-900"
                  title="Quitar filtro precio mínimo"
                >
                  <FaTimes className="text-[10px]" />
                </button>
              </span>
            )}
            {priceMaxActive && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-200">
                Max: {formatCurrency(normalizedMax)}
                <button
                  type="button"
                  onClick={() => onClearPriceFilter('max')}
                  className="hover:text-indigo-900"
                  title="Quitar filtro precio máximo"
                >
                  <FaTimes className="text-[10px]" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-3">Rango de precio (con IVA)</p>
          <div className="px-2 mb-4">
            <Range
              step={5000}
              min={DEFAULT_PRECIO_MIN}
              max={DEFAULT_PRECIO_MAX}
              values={priceRange}
              onChange={handlePriceChange}
              renderTrack={({ props, children }) => (
                <div
                  {...props}
                  className="h-2 w-full rounded-full bg-gray-200"
                  style={{ ...props.style }}
                >
                  <div
                    className="h-2 rounded-full bg-yellow-500"
                    style={{
                      width: `${((priceRange[1] - priceRange[0]) / (DEFAULT_PRECIO_MAX - DEFAULT_PRECIO_MIN)) * 100}%`,
                      marginLeft: `${((priceRange[0] - DEFAULT_PRECIO_MIN) / (DEFAULT_PRECIO_MAX - DEFAULT_PRECIO_MIN)) * 100}%`,
                    }}
                  />
                  {children}
                </div>
              )}
              renderThumb={({ props }) => (
                <div
                  {...props}
                  className="h-5 w-5 rounded-full bg-yellow-500 border-2 border-white shadow-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  style={{ ...props.style }}
                />
              )}
            />
          </div>
          <div className="flex justify-between text-sm font-medium text-gray-600 mb-4">
            <span>{formatCurrency(priceRange[0])}</span>
            <span>{formatCurrency(priceRange[1])}</span>
          </div>
          <button
            type="button"
            onClick={handleApplyPrice}
            disabled={isRefetching}
            className="w-full sm:w-auto px-6 py-2 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-60"
          >
            Aplicar rango de precio
          </button>
        </div>
      )}
    </div>
  );
};

export default BuscadorResultFilters;
